import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCity } from "@/context/CityContext";
import { useCustomer } from "@/context/CustomerContext";
import { VendorCard } from "@/components/VendorCard";
import { Testimonials } from "@/components/Testimonials";
import { Sparkles, Search, ArrowRight, MapPin, Gift, Users, PartyPopper, Store, Heart, Building2 } from "lucide-react";
import { motion } from "framer-motion";

export default function Home() {
  const navigate = useNavigate();
  const { city } = useCity();
  const { customer } = useCustomer();
  const [q, setQ] = useState("");

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: featured = [] } = useQuery({
    queryKey: ["featured", city],
    queryFn: async () =>
      (await api.get("/vendors", { params: { featured: true, ...(city ? { city } : {}) } })).data,
  });
  const { data: allPackages = [] } = useQuery({
    queryKey: ["home-packages"],
    queryFn: async () => (await api.get("/packages")).data,
  });
  const featuredPkgs = allPackages.filter((p) => p.featured);
  const popularPkgs = allPackages.filter((p) => p.popular);
  const packages = (featuredPkgs.length ? featuredPkgs : popularPkgs.length ? popularPkgs : allPackages).slice(0, 8);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/vendors?search=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative px-4 md:px-8 pt-6 md:pt-12">
        <div className="relative overflow-hidden rounded-3xl">
          <img
            src="https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Celebration"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-primary/40" />
          <div className="relative px-6 md:px-14 py-14 md:py-24 max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="overline text-secondary mb-3"
            >
              Every celebration, sorted
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]"
            >
              Book trusted vendors for your big day.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-white/80 mt-4 text-base md:text-lg"
            >
              Decor, DJ, catering, photography & more — pick services from top vendors
              and plan your marriage, mehandi, birthday or baby shower in minutes.
            </motion.p>

            <form onSubmit={submitSearch} className="mt-7 flex items-center gap-2 max-w-md">
              <div className="flex-1 flex items-center gap-2 bg-white rounded-full px-4 h-12 shadow-lg">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="home-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search decorators, caterers, DJs..."
                  className="flex-1 outline-none text-sm bg-transparent"
                />
              </div>
              <button
                data-testid="home-search-btn"
                type="submit"
                className="h-12 w-12 grid place-items-center bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </form>
            {!customer && (
              <div className="mt-5 flex items-center gap-3" data-testid="home-auth-cta">
                <button
                  data-testid="home-login-btn"
                  onClick={() => navigate("/login")}
                  className="h-11 px-5 rounded-full bg-white/15 backdrop-blur text-white font-semibold border border-white/30 hover:bg-white/25 transition-colors"
                >
                  Login
                </button>
                <button
                  data-testid="home-register-btn"
                  onClick={() => navigate("/register")}
                  className="h-11 px-5 rounded-full bg-white text-primary font-semibold hover:bg-white/90 transition-colors"
                >
                  Create account
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Four ways to plan */}
      <section className="px-4 md:px-8 mt-8" data-testid="ways-to-plan">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { to: "/packages", icon: Gift, title: "Event Packages", sub: "All-inclusive, best value" },
            { to: "/occasions", icon: PartyPopper, title: "By Occasion", sub: "Build it, service by service" },
            { to: "/vendors", icon: Store, title: "By Vendor", sub: "Browse trusted local vendors" },
            { to: "/services", icon: Sparkles, title: "By Service", sub: "Decor, DJ, catering & more" },
            { to: "/halls", icon: Building2, title: "Halls & Choultry", sub: "Capacity & pricing upfront" },
            { to: "/decorations", icon: Heart, title: "Budget Decor", sub: "Surprises from ₹2,000" },
          ].map((w, i) => (
            <motion.button
              key={w.to}
              data-testid={`way-${w.to.replace("/", "")}`}
              onClick={() => navigate(w.to)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="text-left bg-card border border-stone-200 rounded-2xl p-4 hover:border-primary hover:-translate-y-0.5 transition-all"
            >
              <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center mb-3">
                <w.icon className="h-5 w-5 text-primary" />
              </div>
              <p className="font-display font-bold leading-tight">{w.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{w.sub}</p>
            </motion.button>
          ))}
        </div>
      </section>

      {/* AI Planner CTA */}
      <section className="px-4 md:px-8 mt-6">
        <div className="tracing-border">
          <button
            data-testid="home-ai-cta"
            onClick={() => navigate("/ai-planner")}
            className="w-full bg-card rounded-[1rem] px-5 md:px-8 py-5 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-display font-bold text-lg">Try the AI Event Planner</p>
                <p className="text-sm text-muted-foreground">
                  Tell us your event & budget — get instant vendor & service picks.
                </p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-primary shrink-0" />
          </button>
        </div>
      </section>

      {/* Book My Event Packages (Category 1) */}
      {packages.length > 0 && (
        <section className="px-4 md:px-8 mt-10" data-testid="home-packages">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="overline text-primary">Book My Event exclusives</p>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight flex items-center gap-2">
                <Gift className="h-6 w-6 text-primary" /> Our event packages
              </h2>
            </div>
            <button
              data-testid="see-all-packages"
              onClick={() => navigate("/packages")}
              className="text-sm font-semibold text-primary flex items-center gap-1"
            >
              See all <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
            {packages.map((p, i) => (
              <motion.button
                key={p.id}
                data-testid={`home-package-${p.id}`}
                onClick={() => navigate("/packages")}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="relative shrink-0 w-64 h-40 rounded-2xl overflow-hidden group text-left"
              >
                <img src={imgUrl(p.image)} alt={p.name} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-1 rounded-full overline bg-secondary text-secondary-foreground">
                  {p.tier}
                </span>
                {p.discount_pct > 0 && p.subtotal > p.price && (
                  <span className="absolute top-3 right-3 text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-500 text-white shadow-lg">
                    Save {inr(p.subtotal - p.price)}
                  </span>
                )}
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <p className="font-display font-extrabold text-xl leading-none">{p.name}</p>
                  <p className="text-white/80 text-[11px] flex items-center gap-1 mt-1">
                    <Users className="h-3 w-3" /> {p.guest_label}
                  </p>
                  <p className="font-semibold text-sm mt-1">{inr(p.price)}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}


      {/* Categories */}
      <section className="px-4 md:px-8 mt-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="overline text-primary">Browse by occasion</p>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
              What are we celebrating?
            </h2>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
          {categories.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`category-${c.slug}`}
              onClick={() => navigate(`/occasions/${c.slug}`)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="relative shrink-0 w-40 h-52 rounded-2xl overflow-hidden group"
            >
              <img src={c.image} alt={c.name} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-left">
                <p className="text-white font-display font-bold text-lg">{c.name}</p>
                <p className="text-white/70 text-xs">{c.tagline}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Featured vendors */}
      <section className="px-4 md:px-8 mt-10 mb-14">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="overline text-primary">Top rated</p>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
              Featured vendors
            </h2>
          </div>
          <button
            data-testid="see-all-vendors"
            onClick={() => navigate("/vendors")}
            className="text-sm font-semibold text-primary flex items-center gap-1"
          >
            See all <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {featured.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm" data-testid="no-featured">
            No featured vendors in {city || "this area"} yet — tap “See all” to explore everyone.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((v, i) => (
              <VendorCard key={v.id} vendor={v} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
